/* eslint-disable @typescript-eslint/no-magic-numbers */
import { CdkOverlayOrigin, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { TooltipModule } from '@libs/ui-lib';
import { fromEvent } from 'rxjs';
import { AutoDraftState } from '../../auto-draft-model';
import { AUTO_DRAFT_OVERLAY_DEFAULT_OFFSET_X } from '../../auto-draft.constants';
import { getClampedOverlayOffsetX } from '../../auto-draft-overlay-position.util';
import { AutoDraftCoordinatorService } from '../../auto-draft-coordinator.service';
import { AutoDraftOverlayComponent } from '../auto-draft-overlay/auto-draft-overlay.component';
import { AutoPromptService } from '../../auto-prompt.service';
import { VolumeReactiveLottieDirective } from '../../directives/volume-reactive-lottie.directive';
import { AutoStartPromptComponent } from '../auto-start-prompt';
import { AutoDraftControlsComponent } from '../auto-draft-controls';

@Component({
  selector: 'lib-auto-draft-button',
  imports: [
    CommonModule,
    SvgIconComponent,
    TranslateModule,
    AutoDraftOverlayComponent,
    OverlayModule,
    CdkDrag,
    LottieComponent,
    VolumeReactiveLottieDirective,
    TooltipModule,
    AutoStartPromptComponent,
    AutoDraftControlsComponent,
  ],
  templateUrl: './auto-draft-button.component.html',
  styleUrl: './auto-draft-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutoDraftButtonComponent implements OnInit, OnDestroy {
  private readonly coordinator = inject(AutoDraftCoordinatorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly autoPromptService = inject(AutoPromptService);

  private readonly trigger = viewChild<CdkOverlayOrigin>('trigger');

  private readonly overlayOffsetX = signal(AUTO_DRAFT_OVERLAY_DEFAULT_OFFSET_X);

  public readonly AutoDraftState = AutoDraftState;
  public readonly offsetX = this.overlayOffsetX.asReadonly();
  public readonly autoDraftState = computed(() => this.coordinator.autoDraftState());
  public readonly buttonState = computed(() => this.coordinator.headerButtonState());
  public readonly hasExistingDraft = computed(() => this.coordinator.activeAutodraft().id !== undefined);
  public readonly isDefaultButtonState = computed(() => this.buttonState() === AutoDraftState.Inactive && !this.hasExistingDraft());
  public readonly showAppointmentIndicator = computed(() => this.coordinator.showAppointmentIndicator());
  public readonly isOverlayOpen = computed(() => this.coordinator.isOverlayOpen());
  public readonly showStartPrompt = computed(() => this.autoPromptService.showStartPrompt());
  public readonly showInlineControls = computed(() => {
    // Pause/Stop/Resume act on the recording session, which only its initiator may control.
    if (this.isOverlayOpen() || this.coordinator.isInitiatedByOther()) {
      return false;
    }

    const state = this.autoDraftState();
    return (
      state === AutoDraftState.InProgress ||
      state === AutoDraftState.Paused ||
      state === AutoDraftState.Loading ||
      state === AutoDraftState.Enhancing
    );
  });
  public readonly initialDragPosition = computed(() => ({
    x: this.coordinator.savedOverlayTranslateX(),
    y: this.coordinator.savedOverlayTranslateY(),
  }));

  public readonly loadingLottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };
  public readonly autodraftInProgressLottieOptions: AnimationOptions = {
    path: '/assets/animations/autodraft-in-progress.json',
    loop: false,
    autoplay: false,
  };

  constructor() {
    // Re-measure whenever the overlay is opened (from anywhere) or resized, so the
    // position can never place it outside the viewport
    effect(() => {
      if (this.isOverlayOpen()) {
        this.updateOverlayOffsetX();
      }
    });
  }

  public ngOnInit(): void {
    this.coordinator.requestCloseOverlay$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.closeOverlay();
    });

    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isOverlayOpen()) {
          this.updateOverlayOffsetX();
        }
      });
  }

  public ngOnDestroy(): void {
    if (this.autoDraftState() === AutoDraftState.InProgress) {
      this.coordinator.commandStopVoiceTyping$.next();
    }
  }

  // Default positions: right-edge aligned to the trigger, so the overlay's right edge stays pinned by CSS
  // (pane.style.right) and the resize math compensates translateX on the 'e' side to keep the left edge fixed.
  public overlayPositions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];

  public toggleOverlay(): void {
    this.updateOverlayOffsetX();
    this.coordinator.setIsDrawerStyle(false);
    this.coordinator.setIsDrawerOpen(false);
    this.coordinator.toggleOverlay();
  }

  public closeOverlay(): void {
    this.coordinator.closeOverlay();
  }

  /**
   * The overlay is anchored to the center of the header button with a fixed horizontal offset, which places it
   * off-screen when the button sits close to the right edge of a narrow viewport. CDK does not correct this on its
   * own: connected-overlay offsets are applied as a CSS transform after positioning, so its push logic works with
   * the un-offset rect. Clamping the offset to the viewport ourselves keeps the overlay fully visible.
   */
  private updateOverlayOffsetX(): void {
    const triggerElement = this.trigger()?.elementRef.nativeElement;

    if (!triggerElement) {
      return;
    }

    const triggerRect = triggerElement.getBoundingClientRect();
    // Matches originX: 'center' of the connected position
    const originX = triggerRect.left + triggerRect.width / 2;

    this.overlayOffsetX.set(getClampedOverlayOffsetX(originX, this.coordinator.savedOverlayWidth(), document.documentElement.clientWidth));
  }

  public onStartPromptStart(): void {
    this.autoPromptService.handleStartAutodraft();
  }

  public onStartPromptExpand(): void {
    this.autoPromptService.handleOpenOverlay();
  }
}
